import CSL from 'citeproc';
import { CitationCache, CSLReference, Citation, CitationIDNoteIndexPair, CitationItem, customDisplayFormat, CitationItemOptions, BibliographyFormatting } from './types';
import { getDefaults } from './csl';
import { bundledLocaleXML } from './csl/bundled-locales';
import { installCutShortEtAl } from './cut-short-et-al';

// Both engines below (renderer and static bibliography) are created from this CSL module.
installCutShortEtAl(CSL);

const { styleXML, localeXML } = getDefaults();

const styleUrl = 'https://www.zotero.org/styles';
const localUrl = 'https://github.com/citation-style-language/locales/raw/6b0cb4689127a69852f48608b6d1a879900f418b/locales-';

/**
 * citeproc's sentinel for "this style has no printed form for what you asked". It comes back as
 * the author-only half of a combined citation on every NUMERIC style, which has no author form at
 * all. It is citeproc's marker, not ours, and it must never reach document content.
 */
const NO_PRINTED_FORM = '[NO_PRINTED_FORM]';

/** Did citeproc actually render something usable, or hand back its own sentinel / nothing? */
const hasPrintedForm = (rendered: string | null | undefined): boolean => {
    const text = (rendered ?? '').trim();
    return text.length > 0 && !text.includes(NO_PRINTED_FORM);
};

/**
 * Normalizes a raw citation string for single-entry bibliography rendering.
 */
const normalizeRawCitation = (value: string): string => value.replaceAll(/\r?\n/g, ' ').trim();

/**
 * Sanitizes raw citation HTML-like content using a conservative allowlist.
 */
const sanitizeRawCitation = (value: string): string => {
    const allowedTags = new Set(['b', 'strong', 'i', 'em', 'sup', 'sub', 'a', 'span', 'br']);
    let sanitized = value;

    sanitized = sanitized.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
    sanitized = sanitized.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '');
    sanitized = sanitized.replace(/<(img|iframe|object|embed|svg|math|form|input|button|textarea|select|option|link|meta|base)\b[^>]*>/gi, '');
    sanitized = sanitized.replace(/\son[a-z-]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '');
    sanitized = sanitized.replace(/\s(href|src)\s*=\s*("|\')?\s*javascript:[^"'\s>]*(\2)?/gi, '');
    sanitized = sanitized.replace(/\s(href|src)\s*=\s*("|\')?\s*data:text\/html[^"'\s>]*(\2)?/gi, '');
    sanitized = sanitized.replace(/<\/?([a-z0-9-]+)([^>]*)>/gi, (match, tagName, attrs = '') => {
        const tag = String(tagName || '').toLowerCase();
        if (!allowedTags.has(tag)) {
            return '';
        }
        const isClosing = match.startsWith('</');
        if (isClosing) {
            return `</${tag}>`;
        }
        if (tag === 'a') {
            const hrefMatch = attrs.match(/\shref\s*=\s*(".*?"|'.*?'|[^\s>]+)/i);
            const hrefRaw = hrefMatch ? hrefMatch[1].trim().replace(/^['"]|['"]$/g, '') : '';
            const hrefSafe = /^(https?:\/\/|mailto:)/i.test(hrefRaw) ? hrefRaw : '';
            const hrefAttr = hrefSafe ? ` href="${hrefSafe}"` : '';
            return `<a${hrefAttr}>`;
        }
        return `<${tag}>`;
    });

    return sanitized.trim();
};

/**
 * Normalizes and sanitizes a raw citation override for bibliography injection.
 */
const prepareRawCitationOverride = (value: unknown): string => {
    if (typeof value !== 'string') {
        return '';
    }
    const normalized = normalizeRawCitation(value);
    if (!normalized) {
        return '';
    }
    return sanitizeRawCitation(normalized);
};

/**
 * Crossref / DOI metadata commonly uses U+2010 (HYPHEN) instead of U+002D
 * (HYPHEN-MINUS). In justified bibliography text, PrinceXML's line-break
 * engine treats U+2010 unevenly and produces visible gaps in the rendered
 * PDF. Word-imported references also occasionally carry U+00AD (SOFT
 * HYPHEN) as a Word artifact, which Prince treats as a discretionary break
 * point and can produce the same gap pattern.
 *
 * Normalize both at the citeproc retrieveItem boundary so every downstream
 * renderer (PDF, JATS, EPUB, HTML, MD) benefits from one change:
 *   - U+2010 → U+002D (ASCII hyphen)
 *   - U+00AD → removed (soft hyphens are never intentional in bib metadata)
 *
 * We deliberately do NOT touch:
 *   - U+2011 (NON-BREAKING HYPHEN) — caller chose non-breaking semantics.
 *   - U+2013 / U+2014 (EN/EM DASH)  — semantically distinct (page ranges,
 *     punctuation) and render correctly.
 *   - The `id` field — it is a machine identifier.
 *   - `URL`, `DOI`, `ISBN`, `ISSN` — link / identifier fields where any
 *     Unicode character is part of the literal value.
 *   - `_meta` — application-side metadata that may carry identifiers.
 *
 * Note: a CSL style file's `<text value="..."/>` can inject literal text
 * after retrieveItem, so a U+2010 baked into a CSL style would bypass this
 * normalization. The bundled APA/Vancouver styles do not do this.
 */
const TYPOGRAPHY_SKIP_KEYS = new Set(['id', 'URL', 'DOI', 'ISBN', 'ISSN', '_meta']);

const normalizeReferenceTypographyValue = (value: unknown): unknown => {
    if (typeof value === 'string') {
        return value.replace(/‐/g, '-').replace(/­/g, '');
    }
    if (Array.isArray(value)) {
        return value.map(normalizeReferenceTypographyValue);
    }
    if (value !== null && typeof value === 'object') {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value)) {
            out[k] = TYPOGRAPHY_SKIP_KEYS.has(k) ? v : normalizeReferenceTypographyValue(v);
        }
        return out;
    }
    return value;
};

const normalizeReferenceTypography = (reference: CSLReference): CSLReference =>
    normalizeReferenceTypographyValue(reference) as CSLReference;

export const getStyleXML = async (id = 'apa'): Promise<string> => {
    const res = await fetch(`${styleUrl}/${id}`);

    if (res.status === 200) {
        return res.text();
    }

    throw new Error('Could not load style');
}

export const getLocaleXML = async (locale = 'en-US') => {
    // Fail fast rather than hang. This runs on the synchronous export path
    // (loadLocaleXML → citation rendering), so an unreachable or slow locale host
    // must time out and let the caller fall back to the bundled en-US terms — a
    // bare fetch with no signal would stall the whole export instead.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
        const res = await fetch(`${localUrl}${locale}.xml`, { signal: controller.signal });
        if (res.status !== 200) { throw new Error('Could not load locale'); }
        return await res.text();
    } finally {
        clearTimeout(timeout);
    }
}

/** Naive cache for network-fetched locale XML, keyed by locale code. */
const localeCache: Record<string, string> = {};

/**
 * Resolves the CSL locale XML for a locale, used to feed citeproc's
 * `retrieveLocale`. Without this, exports fall back to the default en-US terms
 * regardless of the requested locale (e.g. "accessed" instead of "zugegriffen").
 *
 * Resolution order:
 * 1. Bundled locale (offline-safe, {@link bundledLocaleXML})
 * 2. Cached network fetch via {@link getLocaleXML}
 * 3. `fallback` (the en-US default) — on unknown locale or blocked network,
 *    so a locale we cannot resolve degrades to English terms rather than failing.
 *
 * en-US short-circuits to `fallback` (it is the default and always bundled).
 */
export const loadLocaleXML = async (locale = 'en-US', fallback: string = localeXML): Promise<string> => {
    if (!locale || locale === 'en-US') { return fallback; }
    if (bundledLocaleXML[locale]) { return bundledLocaleXML[locale]; }
    if (localeCache[locale]) { return localeCache[locale]; }

    try {
        const xml = await getLocaleXML(locale);
        localeCache[locale] = xml;
        return xml;
    } catch {
        return fallback;
    }
}

export const getCitationOptions = (format: customDisplayFormat): CitationItemOptions => {
    switch (format) {
        case 'author-only':
            return {
                'author-only': true,
                'suppress-author': undefined
            }
        case 'combined':
            return {
                'author-only': true,
                'suppress-author': true
            }
        case 'suppress-author':
            return {
                'author-only': undefined,
                'suppress-author': true
            }
    }

    return {
        'author-only': undefined,
        'suppress-author': undefined
    };
}

/**
 * A stateful reference renderer.
 */
export class ReferenceRenderer {

    id = Math.random().toString(36).slice(0, 8);

    /** The citeproc-js citation renderer */
    private citeproc: CSL.Engine | undefined;

    public footnotes;

    /** Gets the reference it's and note index of already cited references */
    private get pre() {
        return this.citeproc.registry.citationreg.citationByIndex.map((citation) => [citation.citationID, citation.properties?.noteIndex ?? 0]);
    }

    public get citationStyleName(): string {
        return this.citeproc?.cslXml?.dataObj?.children?.find(e => e.name === 'info').children?.find(e => e.name === 'title')?.children?.[0];
    }

    /**
     * Combined (author-only + suppress-author) citations by citation id. citeproc has no such
     * form, so their text is composed from two previews and has to be composed again once all
     * citations are registered.
     */
    private combinedCitations: { [citationID: string]: Citation } = {};

    /**
     * Every call that registered a citation, in order. The bibliography of ALL references is
     * built in a separate engine that replays these, so that adding uncited references to a
     * bibliography never changes how the in-text citations of this renderer read.
     */
    private registeredCalls: Array<{ citation: Citation; atEnd?: boolean; pre?: CitationIDNoteIndexPair[]; post?: CitationIDNoteIndexPair[] }> = [];

    private registeredVersion = 0;

    private allReferencesBibliography: { [ignoreKey: string]: { version: number; value: ReturnType<ReferenceRenderer['getBibliography']>; engine: CSL.Engine; calls: Map<string, Citation> } } = {};

    /** A cache of all rendered citations */
    private citations: {
        [citationID: string]: CitationCache
    } = {};

    get locale(): string {
        return Object.keys(this.citeproc.locale || {})?.[0] || 'en-US';
    }

    constructor(public styleXML: string, public localeXML: string, private references: CSLReference[], options?: { format?: 'text' | 'html', lang?: 'en' | 'de' | string }) {

        let lang: string | undefined = undefined;
        if (options?.lang && options?.lang?.length > 2 && options?.lang[2] === '-') {
            lang = options.lang.slice(0, 2);
        }

        this.lang = lang;
        this.outputFormat = options?.format;
        this.citeproc = this.createEngine();

        this.footnotes = this.citeproc?.cslXml?.dataObj?.attrs?.class === 'note';

    }

    private lang: string | undefined;
    private outputFormat: 'text' | 'html' | undefined;

    /** Creates a citeproc engine for this renderer's style, locale and references. */
    private createEngine(): CSL.Engine {
        const engine = new CSL.Engine({
            retrieveLocale: () => this.localeXML,
            retrieveItem: (id: string) => {
                const reference = this.references.find(ref => ref.id === id);
                if (!reference) { throw new Error('Could not find reference ' + id); }
                const normalizedReference = normalizeReferenceTypography(reference);
                // Zotero uses journalAbbreviation and shortTitle instead of container-title-short and title-short
                if (normalizedReference?.journalAbbreviation?.length > 0 && !normalizedReference?.['container-title-short']) {
                    normalizedReference['container-title-short'] = normalizedReference.journalAbbreviation;
                }
                if (normalizedReference?.shortTitle?.length > 0 && !normalizedReference?.['title-short']) {
                    normalizedReference['title-short'] = normalizedReference.shortTitle;
                }
                return normalizedReference;
            }
        }, this.styleXML, this.lang);
        if (this.outputFormat) {
            engine.setOutputFormat(this.outputFormat);
        }
        return engine;
    }

    /** Renders a bibliography based on all cited references 
     * since creating the renderer. */
    getBibliography(filter?: any): {
        references: string[];
        /** General formating and styling requirements */
        formatting: BibliographyFormatting;
    } {
        return this.bibliographyOf(this.citeproc);
    }

    private bibliographyOf(engine: CSL.Engine): {
        references: string[];
        formatting: BibliographyFormatting;
    } {
        // TODO add filter e.g. to create two bibliogafies for web resources and monographs
        // @see https://citeproc-js.readthedocs.io/en/latest/running.html#makebibliography
        const [formatting, references] = engine.makeBibliography();        
        return {
            references: references.map((reference, i) => {
                const entryId = formatting.entry_ids?.[i]?.[0];
                const entry = entryId
                    ? this.references.find(ref => ref.id === entryId)
                    : undefined;
                const override = prepareRawCitationOverride(entry?.rawCitation);
                if (override.length > 0) {
                    return this.applyRawCitationOverride(reference, override);
                }
                return reference;
            }),
            formatting
        };
    }

    /**
     * Renders a citation in context of the current document
     * (e.g. rendering will change the render result for following references)
     */
    public renderCitation(citation: Citation, metaData?: any): CitationCache {
        return this.addCitationAtEnd(citation, metaData);
    }

    /** Adds a citation to the end of the reference list (no pre or post needed) */
    addCitationAtEnd(citation: Citation, metaData?: any) {
        return this.addCitation(citation, this.pre, [], metaData, true);
    }

    /**
     * Returns a rendered citation and the citation's id  for later reference (e.g. to retrieve an updated citation id string after all references have been processed)
     * (!) adding a citation can always affect previous citations (e.g. because same last names were causing ambiguations)
     */
    addCitationAtPos(citation: Citation, pre: CitationIDNoteIndexPair[], post: CitationIDNoteIndexPair[], metaData?: any): CitationCache {
        return this.addCitation(citation, pre, post, metaData, false);
    }

    private addCitation(citation: Citation, pre: CitationIDNoteIndexPair[], post: CitationIDNoteIndexPair[], metaData: any, atEnd: boolean): CitationCache {

        let isCombined;
        // combined format is not supported by citeproc-js
        // so we build our own string
        if (citation.citationItems?.some(item => item['suppress-author'] && item['author-only'])) {
            citation.citationItems = citation.citationItems.map(item => {
                delete item['suppress-author'];
                delete item['author-only'];

                return item;
            });
            isCombined = true;
        }

        const citationIndex = pre.length;
        // process the reference as normal even if it is combined
        // Use the caller-supplied pre, not the this.pre getter — a caller that
        // tracks note indices independently of the citeproc engine's own
        // registry (e.g. re-scanning a document rather than rebuilding the
        // engine per scan) passes a pre that can legitimately diverge from
        // this.pre, and the note index must follow what was actually passed in.
        const highestIndex = Math.max(...pre.map(c => c[1]), 0) ?? 0;
        citation.properties.noteIndex = highestIndex + 1;
        // Kept for replaying in the all-references bibliography; copied before citeproc adds its
        // own bookkeeping to the citation, which is large and rebuilt on replay.
        const recorded = JSON.parse(JSON.stringify(citation));
        // an explicit position that is simply "after everything registered" is an at-end call
        if (!atEnd && post.length === 0) {
            const current = this.pre;
            atEnd = current.length === pre.length && current.every((pair, i) => pair[0] === pre[i][0] && pair[1] === pre[i][1]);
        }
        const [result, citations] = this.citeproc.processCitationCluster(citation, pre, post);
        recorded.citationID = citation.citationID;
        // processing a citation with nothing before or after it starts the registry over
        if (pre.length === 0 && post.length === 0) { this.registeredCalls = []; }
        this.registeredVersion++;
        // a citation added at the end is replayed after everything registered so far, so its
        // pre list is not stored (it would make the log grow with the square of the citations)
        this.registeredCalls.push(atEnd
            ? { citation: recorded, atEnd: true }
            : { citation: recorded, pre: pre.map(p => [...p] as CitationIDNoteIndexPair), post: post.map(p => [...p] as CitationIDNoteIndexPair) });
        for (let processedCitation of citations) {
            // citeproc also returns earlier citations whose text changed because of this one
            // (e.g. a year suffix once a second work by the same author and year is cited).
            // They keep the meta data they were registered with; only the citation being added
            // takes the meta data of this call.
            const isCurrent = processedCitation[0] === citationIndex;
            this.citations[processedCitation[2]] = {
                index: processedCitation[0],
                renderedCitation: processedCitation[1],
                citationID: processedCitation[2],
                metaData: isCurrent ? metaData : this.citations[processedCitation[2]]?.metaData
            };
        }

        const citationResult = citations.find(([index]) => index === citationIndex);
        if (!citationResult) { throw new Error('Could not get citation from index'); }

        try {
            if (isCombined) {
                this.combinedCitations[citationResult[2]] = { ...citation, citationItems: citation.citationItems.map(item => ({ ...item })) };
                const authorOnly = this.citeproc.previewCitationCluster({
                    ...citation,
                    citationItems: citation.citationItems.map(item => ({ ...item, 'author-only': true }))
                }, pre, post, 'html');
                const supressAuthor = this.citeproc.previewCitationCluster({
                    ...citation,
                    citationItems: citation.citationItems.map(item => ({ ...item, 'suppress-author': true }))
                }, pre, post, 'html');
                // A numeric style has no author-only form, so citeproc answers the first half with
                // its NO_PRINTED_FORM sentinel. Combining is meaningless there — composite mode
                // cannot invent an author that a numeric in-text form does not have — so fall back
                // to the plain citation instead of concatenating a marker into the document.
                const author = hasPrintedForm(authorOnly) ? authorOnly : '';
                return {
                    index: this.citations[citationResult[2]]?.index,
                    renderedCitation: author ? `${author} ${supressAuthor}` : supressAuthor,
                    citationID: this.citations[citationResult[2]]?.citationID
                };
            }
        } catch (e: any) {
            console.error(e);
        }

        return this.citations[citationResult[2]];
    }

    /**
     * Returns the text of a citation as it reads once every citation has been registered
     * (e.g. with the year suffix citeproc only assigns when a later reference is added).
     * Combined citations are composed again from the final state; `undefined` if unknown.
     */
    getFinalRenderedCitation(citationID: string, options?: { withUncited?: boolean; ignoreReferences?: string[] }): string | undefined {
        if (options?.withUncited) {
            const ignore = options.ignoreReferences ?? [];
            // with nothing uncited the cited-only engine already reads exactly like the full list
            if (this.uncitedReferenceIds(ignore).length > 0) {
                // as the citation reads next to a bibliography that prints every reference
                const { engine, calls } = this.allReferencesState(ignore);
                return this.composeCitation(engine, citationID, calls.get(citationID));
            }
        }
        if (!this.combinedCitations[citationID]) { return this.citations[citationID]?.renderedCitation; }
        return this.composeCitation(this.citeproc, citationID, this.combinedCitations[citationID]);
    }

    private uncitedMemo: { version: number; byKey: { [ignoreKey: string]: string[] } } | undefined;

    /** Ids of the references that are not cited by any registered citation. */
    private uncitedReferenceIds(ignore: string[] = []): string[] {
        if (this.uncitedMemo?.version !== this.registeredVersion) {
            this.uncitedMemo = { version: this.registeredVersion, byKey: {} };
        }
        const key = ignore.join('\u0000');
        const known = this.uncitedMemo.byKey[key];
        if (known) { return known; }
        const cited = new Set((this.getBibliography().formatting?.entry_ids || [])
            .map(ids => ids?.[0])
            .filter((id): id is string => typeof id === 'string' && id.length > 0));
        const skipped = new Set(ignore);
        const ids = this.references.map(ref => ref.id).filter(id => !cited.has(id) && !skipped.has(id));
        this.uncitedMemo.byKey[key] = ids;
        return ids;
    }

    /**
     * Composes a citation from the current state of `engine`'s registry, with the citations
     * registered before and after it. `recorded` is the citation as it was registered.
     */
    private composeCitation(engine: CSL.Engine, citationID: string, recorded: Citation | undefined): string | undefined {
        if (!recorded) { return undefined; }
        const all: CitationIDNoteIndexPair[] = engine.registry.citationreg.citationByIndex
            .map((citation: any) => [citation.citationID, citation.properties?.noteIndex ?? 0] as CitationIDNoteIndexPair);
        const position = all.findIndex(([id]) => id === citationID);
        if (position < 0) { return undefined; }
        const pre = all.slice(0, position);
        const post = all.slice(position + 1);
        if (!this.combinedCitations[citationID]) {
            return engine.previewCitationCluster({ ...recorded, citationItems: recorded.citationItems.map(item => ({ ...item })) }, pre, post, this.outputFormat ?? 'html');
        }
        const authorOnly = engine.previewCitationCluster({
            ...recorded,
            citationItems: recorded.citationItems.map(item => ({ ...item, 'author-only': true }))
        }, pre, post, 'html');
        const suppressAuthor = engine.previewCitationCluster({
            ...recorded,
            citationItems: recorded.citationItems.map(item => ({ ...item, 'suppress-author': true }))
        }, pre, post, 'html');
        const author = hasPrintedForm(authorOnly) ? authorOnly : '';
        return author ? `${author} ${suppressAuthor}` : suppressAuthor;
    }

    /** Returns a citation that has previously been added */
    getCitation(citationID: string): CitationCache {
        return this.citations[citationID];
    }

    /**
     * Returns all citations as a map.
     */
    getAllCitations(): { [citationID: string]: CitationCache } {
        return this.citations;
    }

    getBibliographyIdMap() {
        const { references, formatting } = this.getBibliographyFromAll();
        return references.map((plainCitation, index) => {
            const id = formatting.entry_ids?.[index]?.[0];
            // we only fetch the first id because we asume that
            // all references have been rendered with only one id
            return {
                id,
                plainCitation
            };
        });
    }

    /** Renders a citation without respect to previous or following citations
     * (!) This is very fast because rules for ibid and others are not used.
     * Do not use this to create an acurate citation in context of a document context.
     * This could be used in an editor context where it is important to understand what 
     * reference was placed  but not necessarily how it is represented in the final publication.
     */
    public renderCitationOutOfContext(citationItems: CitationItem[]): string {
        return this.citeproc.makeCitationCluster(citationItems);
    }

    /**
     * Returns a bibliography of all references (even unused).
     *
     * Uses `updateUncitedItems` so that items which have never been passed to
     * `processCitationCluster` (i.e. every ref in a document with 0 in-text
     * citations) are still included by `makeBibliography()`.
     *
     * The citeproc-js `updateItems()` approach works when the engine has
     * already been primed by at least one `processCitationCluster` call, but
     * fails silently for a completely unprimed engine — `makeBibliography()`
     * returns an empty list even after calling `updateItems(allIds)`.
     * `updateUncitedItems()` is the designed API for adding items that are
     * never cited in the body text: it registers them in the bibliography
     * registry without touching the in-text citation state, and works
     * regardless of whether the engine has been primed.
     *
     * For a document WITH in-text citations the cited items are already held
     * in the registry from their `processCitationCluster` calls; calling
     * `updateUncitedItems(uncitedIds)` simply adds the rest on top.
     *
     * For a document with 0 in-text citations every id is "uncited", so ALL
     * ids are passed to `updateUncitedItems` and appear in the bibliography.
     */
    public getBibliographyFromAll(options?: { ignoreReferences?: string[] }) {
        return this.allReferencesState(options?.ignoreReferences ?? []).value;
    }

    /**
     * The bibliography of all references, with the engine it was built in and the registered
     * citations by id. Cached until another citation is registered.
     */
    private allReferencesState(ignore: string[] = []) {
        const key = ignore.join('\u0000');
        const cachedState = this.allReferencesBibliography[key];
        if (cachedState?.version === this.registeredVersion) {
            return cachedState;
        }
        const uncitedIds = this.uncitedReferenceIds(ignore);
        // The uncited references are added in a separate engine that first replays every citation
        // registered here. Adding them to THIS engine would change how its in-text citations are
        // disambiguated (a year suffix because of a work that is never cited), and would change
        // citations added later.
        //
        // `updateUncitedItems` adds items to the bibliography registry without clearing the
        // in-text citation state.  Critically it works on an unprimed engine (0 in-text
        // citations) where `updateItems` would leave `makeBibliography()` returning an empty result.
        const engine = this.createEngine();
        for (const call of this.registeredCalls) {
            const pre = call.atEnd
                ? engine.registry.citationreg.citationByIndex.map((c: any) => [c.citationID, c.properties?.noteIndex ?? 0] as CitationIDNoteIndexPair)
                : call.pre ?? [];
            engine.processCitationCluster(JSON.parse(JSON.stringify(call.citation)), pre, call.post ?? []);
        }
        (engine as any).updateUncitedItems(uncitedIds);
        const value = this.bibliographyOf(engine);
        const calls = new Map<string, Citation>();
        for (const call of this.registeredCalls) {
            if (call.citation.citationID) { calls.set(call.citation.citationID, call.citation); }
        }
        const state = { version: this.registeredVersion, value, engine, calls };
        if (Object.values(this.allReferencesBibliography).some(other => other.version !== this.registeredVersion)) { this.allReferencesBibliography = {}; }
        this.allReferencesBibliography[key] = state;
        return state;
    }

    private applyRawCitationOverride(reference: string, override: string): string {
        if (reference?.includes('csl-right-inline')) {
            return reference.replace(
                /(<div class="csl-right-inline">)([\s\S]*?)(<\/div>)/,
                (_, openTag, _content, closeTag) => `${openTag}${override}${closeTag}`
            );
        }
        return `<div class="csl-entry">${override}</div>`;
    }
}

export class SourceField {
    /** Decodes a string in the legacy reference format to a citation. */
    public static fromString(s: string): Citation {
        if (typeof s !== 'string') {
            throw new Error(s + ' is not a string');
        }

        if (s.length === 0) {
            return { citationItems: [], properties: { noteIndex: 0 } };
        }

        if (s.indexOf('%5B') === 0) {
            // encoded [ in the beginning of the string
            s = decodeURI(s);
        }

        if (s.indexOf('[') === 0) {
            // [{  }]
            const info: CitationItem[] = JSON.parse(s);
            if (!Array.isArray(info)) {
                throw new Error('Source must be array but was ' + s);
            }

            // return { citationItems: [{ id: info.map((ref) => { return ref.id; }).toString() }], properties: { noteIndex: 0 } };
            return { citationItems: info.map((ref) => { return ref; }) as unknown as CitationItem[], properties: { noteIndex: 0 } };
        } else {
            return { citationItems: [{ id: s.split(',').filter((v: string) => v !== '').toString() }], properties: { noteIndex: 0 } };
        }
    }

    /** Creates a JSON serialization of all citation items in order */
    public static toString(citation: Citation): string {
        if (!citation) {
            return '';
        }

        return JSON.stringify(citation.citationItems).replace(/"/g, '\"');
    }
}

export class MockRenderer extends ReferenceRenderer {
    constructor(references: any[]) { super(styleXML, localeXML, references); }
}

/**
 * A `CSL.Engine` kept alive between `renderStaticBibliography` calls, with the reference set it
 * resolves against swapped per call.
 *
 * WHY. Constructing the engine parses the whole style XML and the locale, and that — not the
 * rendering — is what a static bibliography call costs. Measured on 2026-09-21 over 50 APA
 * references: 3224 ms with an engine per call (64.5 ms each), 147 ms sharing one engine, plus
 * 117 ms once to build it.
 *
 * The cost lands hardest on the caller that renders ONE reference at a time — anything keeping a
 * rendered citation string beside each record and refreshing it on write does exactly that —
 * because it pays the full parse per reference. At fifty references that is seconds of CPU, long
 * enough to outlive an ordinary HTTP gateway timeout.
 *
 * WHY NOT JUST PASS THE WHOLE BATCH IN ONE CALL. Because citeproc disambiguates across the items
 * in one bibliography: two works by the same author in the same year render `(2020a)` / `(2020b)`
 * together and `(2020)` alone. `renderedApa` is cached per reference, so a batched render would
 * make a stored string depend on whichever references happened to be saved beside it. Rendering
 * each item against its own registry is what keeps the output identical — verified, not assumed;
 * see `references.spec.ts` "engine cache".
 *
 * WHAT MAKES REUSE SAFE. Each call EVICTS the registry (`updateItems([])`) before inserting its
 * own ids, so disambiguation, ordering and entry ids are recomputed from the references it was
 * handed. The one thing that would still leak is a throw mid-render leaving the engine
 * half-updated, so that drops the entry rather than serving it again.
 *
 * THE EVICTION IS LOAD-BEARING, and this comment first claimed it was not — it said "`updateItems`
 * REPLACES the registry, so nothing carries between calls." It replaces the id LIST, not the
 * per-item data. `doinserts` re-reads an item only when the id is absent
 * (`if (!this.registry[item])`), so without the eviction a reused engine asked for an id it
 * already holds serves the CSL it cached on the FIRST call and never reads the swapped-in
 * reference set again. Re-rendering one reference after an edit returned its old string, which
 * is exactly what a caller keeping a rendered citation beside each record does on every write.
 * A consumer's own suite caught it; this library's did not.
 *
 * WHAT IT COSTS. An engine is ~12.5 MB of heap, so the cache holds TWO and no more — see
 * `MAX_CACHED_ENGINES`. Entries are matched by IDENTITY on the style and locale strings, which
 * is why a caller that rebuilds its XML per call quietly gets the old behaviour instead of a
 * 100 KB hash on every call.
 *
 * REENTRANCY. The reference set lives on the cached entry between being set and
 * `makeBibliography` returning, and everything in that window is synchronous — no `await`, and
 * citeproc calls back into `retrieveItem` inline. A second render cannot interleave with it.
 */
interface CachedStaticEngine {
    proc: any;
    /**
     * The style and locale this engine was built for, compared by IDENTITY (`===`), never by
     * value. Two reasons. Hashing a 100 KB style string on every call to use it as a `Map` key
     * would give back a slice of what the cache saves, and callers generally hold their XML on a
     * long-lived object — a module-level cache, or a renderer instance carrying `styleXML` — so
     * the same instance really does come back each time. A caller that rebuilds the string per
     * call simply misses and gets the old behaviour.
     */
    style: any;
    locale: any;
    /** The reference set this engine's `retrieveItem` currently resolves against. */
    current: { references: any[] };
}

/**
 * How many engines to keep. TWO, and the number is a memory budget rather than a preference:
 * a constructed APA engine measures ~12.5 MB of heap (8 held at once = 99.8 MB, 2026-09-21).
 * A cache holding a dozen would cost a couple of hundred megabytes, which is more than a
 * container-sized heap can spare — trading a slow render for an out-of-memory kill, and that is
 * the worse failure because it takes the in-flight work with it.
 *
 * Two rather than one because the entries are kept most-recent-first: one engine would thrash
 * for anything that alternates styles (a batch export across templates), and a miss costs a
 * full rebuild. Two covers the steady state — one style, one locale — plus an alternate.
 */
const MAX_CACHED_ENGINES = 2;

/** Most-recently-used first. At most `MAX_CACHED_ENGINES` long. */
const engineCache: CachedStaticEngine[] = [];

/**
 * The kill-switch. Set `SFO_CITE_ENGINE_CACHE=0` to go back to an engine per call without a
 * deploy of anything else — the cache is meant to be invisible, and an invisible change needs a
 * way to be turned off when something disagrees. Guarded for the browser, where `process` may
 * not exist at all.
 */
const engineCacheEnabled = (): boolean => {
    try {
        // Reached through `globalThis` rather than the bare identifier: this library is built for
        // the browser too, where `process` does not exist and is not typed.
        return (globalThis as any)?.process?.env?.SFO_CITE_ENGINE_CACHE !== '0';
    } catch {
        return true;
    }
};

const createStaticEngine = (style, locale): CachedStaticEngine => {
    const current: { references: any[] } = { references: [] };
    const proc = new CSL.Engine({
        retrieveItem: (id: string) => {
            const reference = current.references.find(r => r.id === id);
            return reference ? normalizeReferenceTypography(reference) : reference;
        },
        retrieveLocale: () => locale,
        retrieveStyle: () => style,
    }, style);
    return { proc, style, locale, current };
};

const getStaticEngine = (style, locale): CachedStaticEngine => {
    if (!engineCacheEnabled()) { return createStaticEngine(style, locale); }
    const index = engineCache.findIndex(e => e.style === style && e.locale === locale);
    if (index >= 0) {
        const [hit] = engineCache.splice(index, 1);
        engineCache.unshift(hit);
        return hit;
    }
    const made = createStaticEngine(style, locale);
    engineCache.unshift(made);
    while (engineCache.length > MAX_CACHED_ENGINES) { engineCache.pop(); }
    return made;
};

/** Forget a cached engine — used when a render threw, so a half-updated engine is never reused. */
const dropStaticEngine = (style, locale): void => {
    const index = engineCache.findIndex(e => e.style === style && e.locale === locale);
    if (index >= 0) { engineCache.splice(index, 1); }
};

/** Test hook — drop every cached engine so a spec can compare a cold engine against a warm one. */
export const __resetCiteEngineCache = (): void => { engineCache.length = 0; };

/** Renders a one-of bibliography by using the order provided */
export const renderStaticBibliography = (references, style, locale) => {
    const engine = getStaticEngine(style, locale);
    const proc = engine.proc;
    //proc.opt.development_extensions.wrap_url_and_doi = true;
    const ids = references.map(r => r.id);

    let result;
    try {
        engine.current.references = references;
        // EVICT FIRST, then insert. citeproc's `doinserts` re-reads an item through
        // `retrieveItem` ONLY when the id is not already in its registry:
        //
        //     if (!this.registry[item]) { Item = this.state.retrieveItem(item); }
        //
        // so a reused engine asked for an id it already holds serves the CSL it cached
        // on the first call and never looks at `engine.current.references` again. Passing
        // an empty list makes `dodeletes` drop every registered id, so the next
        // `updateItems` takes the insert branch and reads the reference we were handed.
        //
        // Without this, re-rendering the SAME reference after its content changed returns
        // the OLD string — which is precisely what a caller keeping a rendered citation
        // beside each record does on every write. A different id was never affected: it
        // gets deleted and re-inserted anyway, which is why this survived a suite that
        // covered repeats, company and interleaved styles but never one id whose content
        // had moved. See `references.spec.ts` "re-renders an id whose content changed".
        proc.updateItems([]);
        proc.updateItems(ids);
        result = proc.makeBibliography();
    } catch (err) {
        dropStaticEngine(style, locale);
        throw err;
    } finally {
        // Do not hold the caller's array alive between calls. `makeBibliography` has already read
        // everything it needs; the mapping below closes over the `references` PARAMETER, not this.
        engine.current.references = [];
    }
    if (!result) { return null; }
    return {
        errors: result[0].bibliography_errors,
        entries: result[1].map((el: string, index: number) => {
            const entryId = result[0].entry_ids?.[index]?.[0];
            const entry = entryId
                ? references.find(r => r.id === entryId)
                : undefined;
            const override = prepareRawCitationOverride(entry?.rawCitation);
            return {
                id: entryId,
                renderedString: override.length > 0
                    ? (el.includes('csl-right-inline')
                        ? el.replace(
                            /(<div class="csl-right-inline">)([\s\S]*?)(<\/div>)/,
                            (_, openTag, _content, closeTag) => `${openTag}${override}${closeTag}`
                        ).trim()
                        : `<div class="csl-entry">${override}</div>`)
                    : el.trim()
            }
        })
    };
};
